
import el from "@cypherpotato/el";
import page from "page";
import { api } from "../api";
import { Button, ButtonRow, DangerButton } from "../components/button";
import { CenteredCard, CenteredCardContent, CenteredCardHeader, CenteredCardNavigation } from "../components/centered-card";
import { Icon } from "../components/icon";
import { IconButton, IconButtonGrid } from "../components/icon-button";
import { Content, Row, Section } from "../components/layout";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableRow } from "../components/table";
import { CheckWrapper, MutedText, Paragraph } from "../components/text";
import { runOperation } from "../ui/operation";
import { popupCenter } from "../ui/popup";
import { randomId } from "../util";
import { Image } from "./image";
import { TextInput } from "./form";
import { throttledCall } from "../ui/throttle";
import { Dropdown, DropdownItem } from "./dropdown";
import { showFlyout } from "../ui/flyout";
import { Card, CardContent, CardNavigation } from "./card";
import { Cached, ClearCache } from "../cache";

export function PrefetchMiniAppData(filterConversations = "") {
    return Cached("mini-app-clients", async () => {
        const response = await api.getJson("/api/v1/mini-app?filterConversations=" + encodeURIComponent(filterConversations));
        return response;
    }, 5000);
}

export function MiniApp(args) {

    const windowCreationOptions = "location=0,toolbar=0,status=0,resizable=true,menubar=0,scrollbars=true,height=500,width=700";
    const miniAppData = el.state.create(null);
    const filterConversations = el.state.create("");

    var sameWindow = args?.sameWindow || false;

    function navigateTo(link) {
        if (sameWindow) {
            page(link);
        } else {
            popupCenter(link, windowCreationOptions);
        }
    }

    async function reloadMiniAppClients() {
        const response = await PrefetchMiniAppData(filterConversations.value);
        miniAppData.value = response.data;
    }

    function createSession(clientId) {
        runOperation(null, async () => {

            localStorage.setItem("mini-app-last-client-id", clientId);

            const response = await api.postJson(`/api/v1/web-chat-client/${clientId}/sessions`, {
                tag: "mini-app-session@" + randomId(),
                expires: 2592000 // 1 month
            });
            navigateTo(`/chat/${response.data.accessKey}`);
        });
        reloadMiniAppClients();
    }

    const DropdownItemIcon = (icon, imageUrl, color) => {

        const RoundedBox = (item) => el("div.rounded-icon-box", item, {
            style: {
                width: "24px",
                height: "24px",
                backgroundColor: color,
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
                outline: "2px solid #66666622"
            }
        });

        if (imageUrl) {
            return RoundedBox(Image(imageUrl, {
                style: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                }
            }));
        } else {
            return RoundedBox(Icon(icon, {
                style: {
                    fontSize: "14px",
                    color: "#ffffff"
                }
            }));
        }
    }

    filterConversations.subscribe(reloadMiniAppClients);

    runOperation("Loading mini-app...", async () => {
        const lastClientId = localStorage.getItem("mini-app-last-client-id");

        if (lastClientId && !args?.noRedirect) {
            const response = await api.postJson(`/api/v1/web-chat-client/${lastClientId}/sessions`, {
                tag: "mini-app-session@" + randomId(),
                expires: 2592000 // 1 month
            });

            if (response?.data?.accessKey) {
                sameWindow = true;
                window.location.replace(`/chat/${response.data.accessKey}?mini-app-redirect=1`);
                return;
            }
        }

        await reloadMiniAppClients();
    });

    return el.state.listening([miniAppData], () =>
        !miniAppData.value ? el("div") : Card(
            {
                classList: ["mini-app-card"]
            },
            args,
            CardNavigation(
                el("img", { src: "/assets/aivax-alt.png" }),
                { style: { justifyContent: 'space-between' } },
                Row(
                    el("div",
                        TextInput(null, "Filter conversations...", {
                            onInput(event) {
                                throttledCall(() => filterConversations.value = event.target.value, 250);
                            },
                            style: { paddingBlock: "8px" }
                        })
                    ),
                    ButtonRow(
                        el.state.listening([miniAppData], () =>
                            Dropdown(
                                "New chat",
                                miniAppData.value && miniAppData.value.clients.map(client =>
                                    DropdownItem(
                                        client.name,
                                        DropdownItemIcon("chat-1-line", client.logoImageUrl, client.primaryColor),
                                        {
                                            style: { alignItems: "center" },
                                            onClick() {
                                                createSession(client.id);
                                            }
                                        }
                                    )
                                )
                            )
                        ),
                        Button(
                            Icon("loop-left-line"),
                            {
                                onClick() {
                                    ClearCache("mini-app-clients");
                                    reloadMiniAppClients(filterConversations.value);
                                }
                            }
                        ),
                        Button(
                            Icon("home-2-line"),
                            {
                                onClick() {
                                    window.open(`/`, "_blank");
                                }
                            }
                        )
                    )
                )
            ),
            Content(
                CardContent(
                    {
                        classList: ["dynamic-size"]
                    },
                    Section(
                        Table(
                            {
                                classList: ["selectable"]
                            },
                            el.state.listening([filterConversations, miniAppData], () =>
                                TableBody(
                                    miniAppData.value?.sessions.map(session =>
                                        TableRow(
                                            TableCell(
                                                Row(
                                                    { style: { alignItems: "center" } },
                                                    el("span.icon-wrapper",
                                                        !session.clientData.logoImageUrl && Icon("chat-1-line"),
                                                        session.clientData.logoImageUrl && Image(session.clientData.logoImageUrl),
                                                        {
                                                            style: {
                                                                backgroundColor: session.clientData.primaryColor
                                                            }
                                                        }
                                                    ),
                                                    el("span", session.title),
                                                    MutedText(" - ", session.clientData.name),
                                                    {
                                                        onClick() {
                                                            navigateTo(`/chat/${session.accessKey}`);
                                                        }
                                                    }
                                                )
                                            ),
                                            TableActionCell(
                                                Dropdown(
                                                    null,
                                                    DropdownItem(
                                                        "Delete conversation",
                                                        Icon("delete-bin-line"),
                                                        {
                                                            onClick() {
                                                                showFlyout(
                                                                    Paragraph("Are you sure you want to delete this conversation? This action cannot be undone."),
                                                                    DangerButton(
                                                                        "Yes, delete conversation",
                                                                        {
                                                                            onClick() {
                                                                                runOperation(null, async () => {
                                                                                    await api.deleteJson(`/api/v1/web-chat-client/${session.clientData.id}/sessions/${session.id}`);
                                                                                    reloadMiniAppClients();
                                                                                });
                                                                            }
                                                                        }
                                                                    )
                                                                )
                                                            }
                                                        }
                                                    )
                                                )
                                            )
                                        )
                                    )?.nullWhenEmpty() || DefaultRow("No conversations found.")
                                )
                            )
                        )
                    )
                )
            )
        )
    )
}
